import { Box, Paper, Stack, Text, rem } from '@mantine/core'
import React, { useState, useRef } from 'react'
import Icon from 'components/Icon/Icon'

const validateImageDimensions = (file: File) =>
  new Promise<File>((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      if (img.width >= 800 && img.height >= 800) {
        resolve(file)
      } else {
        reject(
          new Error(
            `Image must be at least 800x800 pixels. Current size: ${img.width}x${img.height}`
          )
        )
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image'))
    }

    img.src = objectUrl
  })

interface FileUploadProps {
  onFileSelect: (file: File) => void
}

export const FileUpload = ({ onFileSelect }: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (
      file &&
      (file.name.endsWith('.png') ||
        file.name.endsWith('.jpg') ||
        file.name.endsWith('.jpeg'))
    ) {
      try {
        await validateImageDimensions(file)
        onFileSelect(file)
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message)
        }
      }
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
  }

  return (
    <Stack
      w="100%"
      maw={rem(900)}
      mx="auto"
      style={{ cursor: 'pointer' }}
      onClick={() => fileInputRef.current?.click()}
    >
      <Paper
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        withBorder
        radius="10px"
        bg="#EBF2F1"
        py={{ base: 32, lg: 64 }}
        shadow={isDragging ? 'md' : 'sm'}
        style={{
          borderStyle: 'dashed',
          borderWidth: '1px',
          borderColor: '#126554',
          transition: 'all 150ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#dae4e3'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#EBF2F1'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".png, .jpg, .jpeg"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <Stack align="center" gap="md">
          <Text c="#181B19">
            Drop an image anywhere or{' '}
            <span
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              upload a file
            </span>
          </Text>
          <Box my={15}>
            <Icon.UploadSearch />
          </Box>
          {errorMessage ? (
            <Text fz="12px" c="#FF0000">
              {errorMessage}
            </Text>
          ) : (
            <Text fz="12px" c="#545554">
              Supported file types: .jpg, .png. File max size: 25 MB.{' '}
            </Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}
